import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to prefetch data for better performance
 * This will preload data in the background when the user hovers over links
 */
export const usePrefetch = () => {
    const location = useLocation();

    useEffect(() => {
        // Prefetch critical routes when the app loads
        const prefetchRoutes = ['/search', '/cart', '/wishlist'];

        prefetchRoutes.forEach((route) => {
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = route;
            document.head.appendChild(link);
        });

        return () => {
            // Cleanup prefetch links on unmount
            const links = document.querySelectorAll('link[rel="prefetch"]');
            links.forEach((link) => link.remove());
        };
    }, [location]);
};

/**
 * Image optimization utilities
 */
export const optimizeImage = (url: string, width?: number, quality?: number): string => {
    if (!url) return url;

    // Add URL parameters for image optimization if your backend supports it
    const params = new URLSearchParams();
    if (width) params.append('w', width.toString());
    if (quality) params.append('q', quality.toString());

    const hasParams = url.includes('?');
    return params.toString() ? `${url}${hasParams ? '&' : '?'}${params.toString()}` : url;
};

/**
 * Debounce function for search and other input optimizations
 */
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

/**
 * Request idle callback polyfill for better performance
 */
export const requestIdleCallbackPolyfill = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback);
    } else {
        setTimeout(callback, 1);
    }
};

/**
 * Intersection Observer hook for lazy loading
 */
export const useIntersectionObserver = (
    ref: React.RefObject<Element>,
    options?: IntersectionObserverInit
) => {
    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                element.classList.add('visible');
            }
        }, options);

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [ref, options]);
};
